// Staffing planner maths (Dashboard › Planner, Alex 2026-09-21/22).
//
// One input — the DISPATCH TARGET — and the whole flow is worked back
// from it. Every JOB (a department, split by pick type where the speeds
// differ) has WORK still to do, an average RATE per person per hour,
// and the PEOPLE Portal can see on it right now. Time always runs from
// the current clock: there is no start time.
//
// The flow. Work moves shelf drop → picking → (sorting, P&S only) →
// dispatch, and each handoff takes a LAG before the next job can use
// it (a tote has to reach the pick faces, the sorting bays, the pack
// benches). So a job's FINISH-BY time is the target less every lag
// downstream of it — sorting must be done 15 minutes before dispatch,
// picking 15 before sorting (or 15 before dispatch for Single and
// Large, which skip sorting).
//
// Shelf Drop is the exception: it has FIXED finish-by times (Alex,
// 2026-09-22). The AM drop — what was in the queue at 06:00 — must be
// down by 08:30; the PM drop — orders placed after 06:00 — by 13:00.
// The two are one CREW worked in order: the same people clear AM then
// PM, so PM's projection starts where AM's ends and its headcount has
// to cover both piles by 13:00.
//
//   needed  = the smallest whole headcount that clears the work by the
//             job's finish-by time from now (for a crew: the cumulative
//             work by each pile's finish-by, and the crew needs the
//             larger). If every job hits that, the chain lands on the
//             target by construction.
//   finish  = projected with the people actually on the job: when its
//             own capacity would clear the work, or when the last
//             upstream input arrives plus the lag, whichever is later
//             (a downstream job cannot finish before it has been fed).
//
// Everything here is pure so it can be unit-tested without the UI.

export type PlannerDeptKey = "shelf_drop" | "picking" | "sorting" | "dispatch";
export type PlannerPickType = "light" | "pick_and_sort" | "large";
/** Whether a job's work is counted in items (units) or orders. */
export type PlannerUnit = "items" | "orders";
export type PlannerRowKey =
    | "shelf_drop:am" | "shelf_drop:pm"
    | "picking:light" | "picking:pick_and_sort" | "picking:large"
    | "sorting:pick_and_sort"
    | "dispatch:light" | "dispatch:pick_and_sort" | "dispatch:large";

export interface PlannerDeptSpec { key: PlannerDeptKey; labelKey: string; }

export const PLANNER_DEPTS: PlannerDeptSpec[] = [
    { key: "shelf_drop", labelKey: "shelf_drop" },
    { key: "picking",    labelKey: "picking_admin_title" },
    { key: "sorting",    labelKey: "zone_storage" },
    { key: "dispatch",   labelKey: "dispatch" },
];

/** Minutes a handoff takes before the downstream job can use the work. */
export const HANDOFF_MINUTES = {
    shelf_drop_to_picking: 30,
    picking_to_sorting: 15,
    sorting_to_dispatch: 15,
    picking_to_dispatch: 15,
};

/** Shelf Drop's fixed finish-by times. */
export const SHELF_DROP_AM_BY = "08:30";
export const SHELF_DROP_PM_BY = "13:00";

export interface PlannerRowSpec {
    key: PlannerRowKey;
    dept: PlannerDeptKey;
    pickType: PlannerPickType | null;
    /** Shelf Drop: which pile. */
    shift: "am" | "pm" | null;
    unit: PlannerUnit;
    /** Jobs this one feeds, with the handoff lag to each. */
    feeds: Array<{ key: PlannerRowKey; lagMinutes: number }>;
    /** A fixed finish-by ("HH:MM") instead of one worked back from the target. */
    fixedDeadline: string | null;
    /** Rows sharing a crew are worked in order by the same people. */
    crew: string | null;
}

const H = HANDOFF_MINUTES;
const PICKING_FEEDS = [
    { key: "picking:light" as const, lagMinutes: H.shelf_drop_to_picking },
    { key: "picking:pick_and_sort" as const, lagMinutes: H.shelf_drop_to_picking },
    { key: "picking:large" as const, lagMinutes: H.shelf_drop_to_picking },
];
export const PLANNER_ROWS: PlannerRowSpec[] = [
    { key: "shelf_drop:am", dept: "shelf_drop", pickType: null, shift: "am", unit: "items", feeds: PICKING_FEEDS, fixedDeadline: SHELF_DROP_AM_BY, crew: "shelf_drop" },
    { key: "shelf_drop:pm", dept: "shelf_drop", pickType: null, shift: "pm", unit: "items", feeds: PICKING_FEEDS, fixedDeadline: SHELF_DROP_PM_BY, crew: "shelf_drop" },
    { key: "picking:light",         dept: "picking",  pickType: "light",         shift: null, unit: "items",  feeds: [{ key: "dispatch:light", lagMinutes: H.picking_to_dispatch }], fixedDeadline: null, crew: null },
    { key: "picking:pick_and_sort", dept: "picking",  pickType: "pick_and_sort", shift: null, unit: "items",  feeds: [{ key: "sorting:pick_and_sort", lagMinutes: H.picking_to_sorting }], fixedDeadline: null, crew: null },
    { key: "picking:large",         dept: "picking",  pickType: "large",         shift: null, unit: "items",  feeds: [{ key: "dispatch:large", lagMinutes: H.picking_to_dispatch }], fixedDeadline: null, crew: null },
    { key: "sorting:pick_and_sort", dept: "sorting",  pickType: "pick_and_sort", shift: null, unit: "items",  feeds: [{ key: "dispatch:pick_and_sort", lagMinutes: H.sorting_to_dispatch }], fixedDeadline: null, crew: null },
    { key: "dispatch:light",        dept: "dispatch", pickType: "light",         shift: null, unit: "orders", feeds: [], fixedDeadline: null, crew: null },
    { key: "dispatch:pick_and_sort", dept: "dispatch", pickType: "pick_and_sort", shift: null, unit: "orders", feeds: [], fixedDeadline: null, crew: null },
    { key: "dispatch:large",        dept: "dispatch", pickType: "large",         shift: null, unit: "orders", feeds: [], fixedDeadline: null, crew: null },
];

export const DEFAULT_DISPATCH_CUTOFF = "15:30";
/** Aim to be done this long before the cut-off (Alex: "15 mins earlier to allow some room"). */
export const DEFAULT_BUFFER_MINUTES = 15;

export interface PlannerRowInput {
    /** Units (items / orders) still to clear. */
    work: number;
    /** Average units per person per hour. */
    rate: number;
    /** People on the job right now (a crew's rows carry the same figure). */
    people: number;
}

export interface PlannerInput {
    /** Minutes from midnight, the current wall clock. */
    nowMinutes: number;
    /** "HH:MM" the last order must be packed by. */
    target: string;
    rows: Record<PlannerRowKey, PlannerRowInput>;
}

export type PlannerRowStatus =
    /** Nothing to do. */
    | "nothing"
    /** Its finish-by time has already passed with work left. */
    | "no_time"
    /** Work but no rate — nothing can be estimated. */
    | "no_rate"
    /** Work, a rate, but nobody on it. */
    | "nobody"
    /** Projected to finish by its finish-by time. */
    | "on_track"
    /** Projected to finish after its finish-by time. */
    | "late";

export interface PlannerRowResult {
    key: PlannerRowKey;
    dept: PlannerDeptKey;
    crew: string | null;
    status: PlannerRowStatus;
    /** Minutes from midnight this job must be done by. */
    deadlineMinutes: number;
    /** Hours from now to the finish-by time, never negative. */
    hoursAvailable: number;
    /** Smallest whole headcount that clears the work (a crew: the work so far) by the finish-by time; null when it can't be computed. */
    needed: number | null;
    /** needed − people, floored at 0; null when needed is null. */
    shortfall: number | null;
    /**
     * Projected finish with the people on it, minutes from midnight:
     * Infinity when someone in the chain has work and nobody on it;
     * null when there is nothing to do or a rate is missing in the chain.
     */
    finishMinutes: number | null;
    /** How far past the finish-by time the projection lands, minutes (0 when on time); null when not estimable. */
    lateMinutes: number | null;
}

export type PlannerFlowStatus = "nothing" | "unknown" | "blocked" | "on_track" | "late";

export interface PlannerResult {
    rows: PlannerRowResult[];
    targetMinutes: number;
    /** Minutes from now to the target, never negative. */
    minutesLeft: number;
    /** Sum over jobs; a crew counts once, at the larger of its piles' needs. */
    totalNeeded: number;
    /** Sum over jobs; a crew counts once. */
    totalPeople: number;
    /** true when every job has a computable headcount. */
    complete: boolean;
    /** true when no job has any work. */
    empty: boolean;
    lateCount: number;
    /** Projected time the last dispatch job finishes; null when nothing / unknown / blocked. */
    dispatchFinishMinutes: number | null;
    dispatchStatus: PlannerFlowStatus;
    /** Minutes past the target the projected dispatch finish lands; 0 when on time. */
    dispatchLateMinutes: number;
}

const clampNonNeg = (n: number) => (Number.isFinite(n) && n > 0 ? n : 0);

/** "08:30" → 510. Anything malformed → null. */
export const parseClock = (hhmm: string): number | null => {
    const m = /^(\d{1,2}):(\d{2})$/.exec(hhmm.trim());
    if (!m) return null;
    const h = Number(m[1]);
    const min = Number(m[2]);
    if (h > 23 || min > 59) return null;
    return h * 60 + min;
};

/** 510 → "08:30". Minutes past midnight wrap onto the next day. */
export const formatClock = (minutes: number): string => {
    const m = Math.round(minutes) % (24 * 60);
    const h = Math.floor(m / 60);
    return `${String(h).padStart(2, "0")}:${String(m % 60).padStart(2, "0")}`;
};

/** 95 → "1h 35m", 40 → "40m", 120 → "2h". */
export const formatDuration = (minutes: number): string => {
    const m = Math.max(0, Math.round(minutes));
    const h = Math.floor(m / 60);
    const rest = m % 60;
    if (h === 0) return `${rest}m`;
    if (rest === 0) return `${h}h`;
    return `${h}h ${rest}m`;
};

/** Minutes from midnight on the local wall clock. */
export const clockMinutesOf = (ms: number): number => {
    const d = new Date(ms);
    return d.getHours() * 60 + d.getMinutes() + d.getSeconds() / 60;
};

/** Default dispatch target: the cut-off less the buffer. */
export const defaultTarget = (
    cutoff: string = DEFAULT_DISPATCH_CUTOFF,
    bufferMinutes: number = DEFAULT_BUFFER_MINUTES,
): string => {
    const c = parseClock(cutoff) ?? (parseClock(DEFAULT_DISPATCH_CUTOFF) as number);
    return formatClock(Math.max(0, c - clampNonNeg(bufferMinutes)));
};

/**
 * Smallest whole headcount that clears `work` at `rate` per person-hour
 * inside `hours`. 0 when there is nothing to do; null when it can't be
 * computed (no rate, or no time).
 */
export const peopleNeeded = (work: number, rate: number, hours: number): number | null => {
    const w = clampNonNeg(work);
    if (w === 0) return 0;
    if (clampNonNeg(rate) === 0 || clampNonNeg(hours) === 0) return null;
    return Math.ceil(w / (rate * hours));
};

/** Hours for `people` at `rate` each to clear `work`; 0 for no work; null when not estimable. */
export const hoursToFinish = (work: number, rate: number, people: number): number | null => {
    const w = clampNonNeg(work);
    if (w === 0) return 0;
    if (clampNonNeg(rate) === 0 || clampNonNeg(people) === 0) return null;
    return w / (rate * people);
};

/** Each job's finish-by time: fixed where it has one, else the target less every lag downstream of it. */
export const deadlinesFor = (targetMinutes: number): Record<PlannerRowKey, number> => {
    const memo = new Map<PlannerRowKey, number>();
    const specOf = (key: PlannerRowKey) => PLANNER_ROWS.find((r) => r.key === key) as PlannerRowSpec;
    const of = (key: PlannerRowKey): number => {
        const hit = memo.get(key);
        if (hit !== undefined) return hit;
        const spec = specOf(key);
        const fixed = spec.fixedDeadline !== null ? parseClock(spec.fixedDeadline) : null;
        const v = fixed !== null
            ? fixed
            : spec.feeds.length === 0
                ? targetMinutes
                : Math.min(...spec.feeds.map((f) => of(f.key) - f.lagMinutes));
        memo.set(key, v);
        return v;
    };
    const out = {} as Record<PlannerRowKey, number>;
    for (const r of PLANNER_ROWS) out[r.key] = of(r.key);
    return out;
};

export const plan = (input: PlannerInput): PlannerResult => {
    const targetMinutes = parseClock(input.target) ?? (parseClock(defaultTarget()) as number);
    const now = input.nowMinutes;
    const deadlines = deadlinesFor(targetMinutes);

    // Work each job must clear before it is done: its own, plus — for a
    // crew's later piles — everything the crew works through first.
    const cumulative = new Map<PlannerRowKey, number>();
    const crewSoFar = new Map<string, number>();
    for (const spec of PLANNER_ROWS) {
        const own = clampNonNeg(input.rows[spec.key].work);
        if (spec.crew === null) { cumulative.set(spec.key, own); continue; }
        const before = crewSoFar.get(spec.crew) ?? 0;
        cumulative.set(spec.key, before + own);
        crewSoFar.set(spec.crew, before + own);
    }

    // Per-job capacity finish (its cumulative work, its people), ignoring inputs:
    //   null      — nothing to do (transparent in the chain)
    //   undefined — work but no rate (unknown)
    //   Infinity  — work but nobody on it
    const capacity = new Map<PlannerRowKey, number | null | undefined>();
    for (const spec of PLANNER_ROWS) {
        const r = input.rows[spec.key];
        const work = clampNonNeg(r.work);
        const cum = cumulative.get(spec.key) as number;
        const rate = clampNonNeg(r.rate);
        const people = clampNonNeg(r.people);
        if (work === 0) capacity.set(spec.key, null);
        else if (rate === 0) capacity.set(spec.key, undefined);
        else if (people === 0) capacity.set(spec.key, Infinity);
        else capacity.set(spec.key, now + (cum / (rate * people)) * 60);
    }

    // Projected finish = later of own capacity and the last upstream
    // arrival (+ lag). Unknown anywhere upstream makes this unknown too.
    const finishMemo = new Map<PlannerRowKey, number | null | undefined>();
    const finishOf = (key: PlannerRowKey): number | null | undefined => {
        if (finishMemo.has(key)) return finishMemo.get(key);
        const candidates: number[] = [];
        let unknown = false;
        const own = capacity.get(key);
        if (own === undefined) unknown = true;
        else if (own !== null) candidates.push(own);
        for (const up of PLANNER_ROWS) {
            const feed = up.feeds.find((f) => f.key === key);
            if (!feed) continue;
            const upFinish = finishOf(up.key);
            if (upFinish === undefined) unknown = true;
            else if (upFinish !== null) candidates.push(upFinish + feed.lagMinutes);
        }
        const v = unknown ? undefined : candidates.length === 0 ? null : Math.max(...candidates);
        finishMemo.set(key, v);
        return v;
    };

    const rows: PlannerRowResult[] = PLANNER_ROWS.map((spec) => {
        const r = input.rows[spec.key];
        const work = clampNonNeg(r.work);
        const cum = cumulative.get(spec.key) as number;
        const rate = clampNonNeg(r.rate);
        const people = clampNonNeg(r.people);
        const deadlineMinutes = deadlines[spec.key];
        const hoursAvailable = Math.max(0, deadlineMinutes - now) / 60;
        const base = {
            key: spec.key, dept: spec.dept, crew: spec.crew, deadlineMinutes, hoursAvailable,
            needed: null, shortfall: null, finishMinutes: null, lateMinutes: null,
        };
        if (work === 0) return { ...base, status: "nothing", needed: 0, shortfall: 0 };
        if (rate === 0) return { ...base, status: "no_rate" };
        if (hoursAvailable === 0) return { ...base, status: "no_time" };
        const needed = peopleNeeded(cum, rate, hoursAvailable) as number;
        const shortfall = Math.max(0, needed - people);
        const finish = finishOf(spec.key);
        if (people === 0) return { ...base, status: "nobody", needed, shortfall, finishMinutes: Infinity };
        if (finish === undefined || finish === null) return { ...base, status: "no_rate", needed, shortfall };
        const lateMinutes = Math.max(0, finish - deadlineMinutes);
        return {
            ...base,
            status: lateMinutes > 0.5 ? "late" : "on_track",
            needed,
            shortfall,
            finishMinutes: finish,
            lateMinutes: Number.isFinite(lateMinutes) ? Math.round(lateMinutes) : null,
        };
    });

    // The flow's verdict = the dispatch jobs' projections.
    const dispatchFinishes = PLANNER_ROWS.filter((r) => r.dept === "dispatch").map((r) => finishOf(r.key));
    let dispatchStatus: PlannerFlowStatus;
    let dispatchFinishMinutes: number | null = null;
    if (dispatchFinishes.some((f) => f === undefined)) dispatchStatus = "unknown";
    else if (dispatchFinishes.some((f) => f === Infinity)) dispatchStatus = "blocked";
    else {
        const finite = dispatchFinishes.filter((f): f is number => typeof f === "number");
        if (finite.length === 0) dispatchStatus = "nothing";
        else {
            dispatchFinishMinutes = Math.max(...finite);
            dispatchStatus = dispatchFinishMinutes - targetMinutes > 0.5 ? "late" : "on_track";
        }
    }

    // Totals: a crew's rows share people and its need is the larger pile's.
    let totalNeeded = 0;
    let totalPeople = 0;
    const crewNeeded = new Map<string, number>();
    const crewPeople = new Map<string, number>();
    for (const spec of PLANNER_ROWS) {
        const row = rows.find((x) => x.key === spec.key) as PlannerRowResult;
        const people = clampNonNeg(input.rows[spec.key].people);
        if (spec.crew === null) {
            totalNeeded += row.needed ?? 0;
            totalPeople += people;
            continue;
        }
        crewNeeded.set(spec.crew, Math.max(crewNeeded.get(spec.crew) ?? 0, row.needed ?? 0));
        crewPeople.set(spec.crew, Math.max(crewPeople.get(spec.crew) ?? 0, people));
    }
    for (const n of crewNeeded.values()) totalNeeded += n;
    for (const n of crewPeople.values()) totalPeople += n;

    return {
        rows,
        targetMinutes,
        minutesLeft: Math.max(0, targetMinutes - now),
        totalNeeded,
        totalPeople,
        complete: rows.every((r) => r.needed !== null),
        empty: rows.every((r) => r.status === "nothing"),
        lateCount: rows.filter((r) => r.status === "late").length,
        dispatchFinishMinutes,
        dispatchStatus,
        dispatchLateMinutes: dispatchFinishMinutes === null ? 0 : Math.max(0, Math.round(dispatchFinishMinutes - targetMinutes)),
    };
};
